import { portalApi, TAGS } from "api/rtkApi";
import { SERVICES } from "constants/environment";
import { preparePaginationParams } from "utils/preparePaginationParams";

import { prepareSettlementsFilters } from "../utils/prepareSettlementsFilters";

import type { RTKMeta } from "models/RTKMeta";
import type { CreatedByParams } from "modules/common/models/CreatedBy";
import type { UUID } from "utils";
import type { ObjectWithId } from "utils/ObjectWithId";
import type { PaginationParams } from "utils/preparePaginationParams";

import type {
  OrderableColumns,
  Settlement,
  SettlementDetails,
  TriggerSettlementCode,
} from "../models/Settlements";
import type { SettlementsFilters } from "../models/SettlementsFilters";

type GetSettlementsRequest = Pick<
  PaginationParams<OrderableColumns>,
  "limit" | "order" | "page" | "search" | "sortBy"
> &
  CreatedByParams & {
    customerHierarchyId?: UUID;
    filters?: Partial<SettlementsFilters>;
  };

type GetSettlementsResponse = {
  settlements: Settlement[];
  totalItems: number;
};

type PostSettlementRequest = {
  customerId: UUID;
};

type PostSettlementResponse = {
  code: TriggerSettlementCode;
  message: string;
};

type PostSettleAccountRequest = {
  accountId: UUID;
};

type PostSettleAccountResponse = {
  message: string;
};

type GetSettlementDetailsRequest = ObjectWithId;
type GetSettlementDetailsResponse = SettlementDetails;

type GetDownloadSettlementReportRequest = ObjectWithId;
type GetDownloadSettlementReportResponse = string;

const extendedApi = portalApi.injectEndpoints({
  endpoints: (builder) => ({
    getSettlements: builder.query<
      GetSettlementsResponse,
      GetSettlementsRequest
    >({
      query: ({ filters, startDate, endDate, search, ...params }) => ({
        url: "/portal/cash-management/settlements",
        params: {
          ...preparePaginationParams(params),
          ...prepareSettlementsFilters({
            ...filters,
            startDate,
            endDate,
            search,
          }),
        },
        method: "GET",
      }),
      transformResponse: (settlements: Settlement[], meta: RTKMeta) => ({
        settlements,
        totalItems: Number(meta.response.headers.get("total-items")),
      }),
      providesTags: [TAGS.CASH_MANAGEMENT_SETTLEMENTS],
      extraOptions: {
        service: SERVICES.CONNECT,
      },
    }),
    postSettlement: builder.mutation<
      PostSettlementResponse,
      PostSettlementRequest
    >({
      query: ({ customerId }) => ({
        url: `/customers/${customerId}/settlement/trigger`,
        method: "POST",
      }),
      invalidatesTags: [TAGS.CASH_MANAGEMENT_SETTLEMENTS],
      extraOptions: {
        service: SERVICES.CONNECT,
      },
    }),
    getSettlementDetails: builder.query<
      GetSettlementDetailsResponse,
      GetSettlementDetailsRequest
    >({
      providesTags: (_, __, { id }) => [
        { type: TAGS.CASH_MANAGEMENT_SETTLEMENT_DETAILS, id },
      ],
      query: ({ id }) => ({
        url: `/portal/cash-management/settlements/${id}`,
      }),
      extraOptions: {
        service: SERVICES.CONNECT,
      },
    }),
    postSettleAccount: builder.mutation<
      PostSettleAccountResponse,
      PostSettleAccountRequest
    >({
      query: ({ accountId }) => ({
        url: `/portal/settlements/${accountId}/settle`,
        method: "POST",
      }),
      invalidatesTags: [TAGS.CASH_MANAGEMENT_SETTLEMENTS],
      extraOptions: {
        service: SERVICES.CONNECT,
      },
    }),
    getDownloadSettlementReport: builder.query<
      GetDownloadSettlementReportResponse,
      GetDownloadSettlementReportRequest
    >({
      providesTags: (_, __, { id }) => [
        { type: TAGS.CASH_MANAGEMENT_SETTLEMENT_REPORT, id },
      ],
      query: ({ id }) => ({
        url: `/portal/cash-management/settlements/${id}/report/download`,
        method: "GET",
        responseHandler: (response) =>
          response.ok ? response.text() : response.json(),
        cache: "no-store",
      }),
      extraOptions: {
        service: SERVICES.CONNECT,
      },
    }),
  }),
});

export const {
  useGetSettlementsQuery,
  useGetSettlementDetailsQuery,
  useLazyGetDownloadSettlementReportQuery,
  usePostSettlementMutation,
  usePostSettleAccountMutation,
} = extendedApi;
